import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FaTooth } from 'react-icons/fa';
import { BiPlus, BiCheck, BiX } from 'react-icons/bi';
import { RiDeleteBinLine } from 'react-icons/ri';
import { FiEdit2 } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import {
  getHistorialOdontogramaByPatientId,
  getHistorialOdontograma,
  getHistorialPatientId,
} from '../../redux/features/odontogramaSlice';
import {
  getNotasByCitaId,
  saveNotasCita,
  getAllNotasCita,
  getNotasCitaStatus,
  getNotasCitaId,
} from '../../redux/features/notasCitaSlice';

const ESTADO_OPTIONS = [
  { value: 'avance', label: 'En avance' },
  { value: 'completado', label: 'Completado' },
];

function EstadoBadge({ estado }) {
  if (!estado || estado === 'avance')
    return (
      <span className="text-xs border border-subMain text-subMain rounded-full px-2 py-0.5">
        En avance
      </span>
    );
  return (
    <span className="text-xs border border-green-500 text-green-600 rounded-full px-2 py-0.5">
      Completado
    </span>
  );
}

export default function NotasCita({ cita }) {
  const dispatch = useDispatch();
  const historial = useSelector(getHistorialOdontograma);
  const historialPatientId = useSelector(getHistorialPatientId);
  const notasCita = useSelector(getAllNotasCita);
  const notasCitaId = useSelector(getNotasCitaId);
  const status = useSelector(getNotasCitaStatus);

  // Map: odontograma_id → { nota, estado }
  const [notasMap, setNotasMap] = useState({});
  // Pieza actualmente en edición
  const [editingId, setEditingId] = useState(null);
  // Borrador mientras se edita
  const [draft, setDraft] = useState({ nota: '', estado: 'avance' });

  useEffect(() => {
    if (cita?.patient_id && historialPatientId !== cita.patient_id) {
      dispatch(getHistorialOdontogramaByPatientId(cita.patient_id));
    }
    if (cita?.id && notasCitaId !== cita.id) {
      dispatch(getNotasByCitaId(cita.id));
    }
  }, [cita?.id, cita?.patient_id, dispatch, historialPatientId, notasCitaId]);

  // Pre-fill desde notas ya guardadas de esta re consulta
  useEffect(() => {
    if (notasCita?.length > 0) {
      const map = {};
      notasCita.forEach((n) => {
        map[n.odontograma_id] = { nota: n.nota || '', estado: n.estado || 'avance' };
      });
      setNotasMap(map);
    }
  }, [notasCita]);

  const ultimaConsulta = useMemo(() => {
    if (!historial?.length) return null;
    return [...historial].filter(h => h.cita_id < cita.id).at(-1) ?? null;
  }, [historial, cita?.id]);

  // Guarda todas las notas actuales del map
  const guardarTodo = (mapActual) => {
    const notas = Object.entries(mapActual)
      .map(([odontogramaId, v]) => ({
        odontograma_id: parseInt(odontogramaId, 10),
        nota: v.nota || '',
        estado: v.estado || 'avance',
      }));

    return dispatch(saveNotasCita({ cita_id: cita.id, notas }));
  };

  const handleConfirmar = (odontogramaId) => {
    if (!draft.nota?.trim()) {
      toast.error('La nota no puede estar vacía');
      return;
    }
    setNotasMap((prev) => ({
      ...prev,
      [odontogramaId]: { nota: draft.nota, estado: draft.estado },
    }));
    setEditingId(null);
  };

  const handleEliminar = (odontogramaId) => {
    setNotasMap((prev) => {
      const nuevoMap = { ...prev };
      delete nuevoMap[odontogramaId];
      return nuevoMap;
    });
  };

  const abrirEdicion = (odontogramaId) => {
    const existente = notasMap[odontogramaId];
    setDraft(existente ? { ...existente } : { nota: '', estado: 'avance' });
    setEditingId(odontogramaId);
  };

  const handleGuardarTodo = () => {
    if (Object.keys(notasMap).length === 0) {
      toast.error('No hay notas para guardar');
      return;
    }
    guardarTodo(notasMap)
      .unwrap()
      .then(() => toast.success('Notas guardadas'))
      .catch(() => toast.error('Error al guardar'));
  };

  if (!ultimaConsulta) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-textGray">
        <FaTooth className="text-4xl mb-3 opacity-20" />
        <p className="text-sm">No hay odontograma registrado para este paciente</p>
        <p className="text-xs mt-1 opacity-70">Registre un odontograma en la primera consulta</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Consulta original */}
      <div className="rounded-lg bg-dry px-3 py-2">
        <p className="text-xs text-textGray uppercase tracking-wide font-medium mb-0.5">Consulta original</p>
        <p className="text-xs font-medium text-main">{ultimaConsulta.fecha}</p>
        <p className="text-xs text-textGray">
          {ultimaConsulta.piezas.length} pieza{ultimaConsulta.piezas.length !== 1 ? 's' : ''}
        </p>
      </div>

      {/* Informe de piezas — re consulta actual */}
      <div className="px-1">
        <p className="text-xs text-textGray uppercase tracking-wide font-medium mb-0.5">Informe de piezas - Reconsulta</p>
        {cita?.motivo && (
          <p className="text-sm font-semibold text-main leading-snug">{cita.motivo}</p>
        )}
        {cita?.fecha && (
          <p className="text-xs text-textGray">
            {new Intl.DateTimeFormat('es-BO', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(new Date(cita.fecha))}
          </p>
        )}
      </div>

      <div className="overflow-y-auto pr-1" style={{ maxHeight: 'calc(60vh - 160px)' }}>
      <div className="border border-border rounded-xl overflow-hidden">
        {ultimaConsulta.piezas.map((pieza, idx) => {
          const notaGuardada = notasMap[pieza.odontograma_id];
          const estaEditando = editingId === pieza.odontograma_id;

          return (
            <div
              key={idx}
              className={`px-4 py-3 flex gap-4 items-start bg-white ${
                idx < ultimaConsulta.piezas.length - 1 ? 'border-b border-border' : ''
              }`}
            >
              {/* Badge pieza */}
              <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
                <div className="bg-text text-subMain w-8 h-8 rounded-lg flex items-center justify-center mt-0.5">
                  <FaTooth className="text-sm" />
                </div>
                <span className="text-xs font-medium text-main">{pieza.diente}</span>
              </div>

              <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                {/* Referencia */}
                <div className="flex flex-col gap-0.5">
                  {pieza.diagnostico && (
                    <p className="text-xs text-main">
                      <span className="font-medium">Diagnóstico:</span> {pieza.diagnostico}
                    </p>
                  )}
                  {pieza.tratamiento && (
                    <p className="text-xs text-main">
                      <span className="font-medium">Tratamiento:</span> {pieza.tratamiento}
                    </p>
                  )}
                </div>

                {/* Nota guardada (modo lectura) */}
                {notaGuardada && !estaEditando && (
                  <div className="relative bg-dry rounded-lg px-3 py-2 pr-16">
                    <span className="absolute top-2 right-2">
                      <EstadoBadge estado={notaGuardada.estado} />
                    </span>
                    <p className="text-xs text-main">{notaGuardada.nota}</p>
                    <div className="flex gap-1 mt-1">
                      <button
                        onClick={() => abrirEdicion(pieza.odontograma_id)}
                        className="text-textGray hover:text-subMain p-1"
                        title="Editar nota"
                      >
                        <FiEdit2 className="text-sm" />
                      </button>
                      <button
                        onClick={() => handleEliminar(pieza.odontograma_id)}
                        className="text-textGray hover:text-red-500 p-1"
                        title="Eliminar nota"
                      >
                        <RiDeleteBinLine className="text-sm" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Formulario edición / nueva nota */}
                {estaEditando && (
                  <div className="flex flex-col gap-2 bg-dry rounded-lg px-3 py-2">
                    <textarea
                      rows={2}
                      autoFocus
                      placeholder="Nota de avance..."
                      value={draft.nota}
                      onChange={(e) => setDraft((p) => ({ ...p, nota: e.target.value }))}
                      className="w-full text-xs border border-border rounded-lg p-2 focus:outline-none focus:border-subMain resize-none text-main placeholder-textGray bg-white"
                    />
                    <div className="flex items-center justify-between gap-2">
                      <select
                        value={draft.estado}
                        onChange={(e) => setDraft((p) => ({ ...p, estado: e.target.value }))}
                        className="text-xs border border-border rounded-lg p-2 focus:outline-none focus:border-subMain text-main bg-white"
                      >
                        {ESTADO_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <div className="flex gap-1">
                        <button
                          onClick={() => handleConfirmar(pieza.odontograma_id)}
                          className="flex items-center gap-1 bg-subMain text-white text-xs rounded-lg px-3 py-1.5 hover:opacity-90"
                        >
                          <BiCheck className="text-base" /> Agregar
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="text-textGray hover:text-main p-1.5"
                        >
                          <BiX className="text-base" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Botón agregar si no tiene nota y no está editando */}
                {!notaGuardada && !estaEditando && (
                  <button
                    onClick={() => abrirEdicion(pieza.odontograma_id)}
                    className="flex items-center gap-1 text-xs text-subMain hover:opacity-80 self-start"
                  >
                    <BiPlus className="text-sm" /> Agregar nota
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      </div>

      <div className="flex justify-end pt-1 pb-1">
        <button
          onClick={handleGuardarTodo}
          disabled={status === 'loading'}
          className="flex items-center gap-2 bg-subMain text-white text-sm rounded-lg px-4 py-2 hover:opacity-90 disabled:opacity-50 transition-opacity"
        >
          <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 448 512" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path d="M433.941 129.941l-83.882-83.882A48 48 0 0 0 316.118 32H48C21.49 32 0 53.49 0 80v352c0 26.51 21.49 48 48 48h352c26.51 0 48-21.49 48-48V163.882a48 48 0 0 0-14.059-33.941zM224 416c-35.346 0-64-28.654-64-64 0-35.346 28.654-64 64-64s64 28.654 64 64c0 35.346-28.654 64-64 64zm96-304.52V212c0 6.627-5.373 12-12 12H76c-6.627 0-12-5.373-12-12V108c0-6.627 5.373-12 12-12h228.52c3.183 0 6.235 1.264 8.485 3.515l3.48 3.48A11.996 11.996 0 0 1 320 111.48z"></path></svg>
          {status === 'loading' ? 'Guardando...' : 'Guardar notas'}
        </button>
      </div>
    </div>
  );
}
